import type { ErrorCode, ReasonCode } from "@agentry/zerovia-contracts";
import { ErrorCodes, ReasonCodes } from "@agentry/zerovia-contracts";

/**
 * Backendの`error.code`/`error.reason`をそのまま保持する。
 * UIはMessage文字列ではなく`reason`で復旧導線を分岐する。
 */
export class ApiError extends Error {
	readonly code: ErrorCode | string;
	readonly reason: ReasonCode | string | undefined;
	readonly status: number;
	readonly details: unknown;

	constructor(init: {
		code: ErrorCode | string;
		message: string;
		reason?: string;
		status?: number;
		details?: unknown;
	}) {
		super(init.message);
		this.name = "ApiError";
		this.code = init.code;
		this.reason = init.reason;
		this.status = init.status ?? httpStatusFor(init.code);
		this.details = init.details;
	}

	static notFound(message = "Not found", reason?: string): ApiError {
		return new ApiError({ code: ErrorCodes.NOT_FOUND, message, reason });
	}

	static forbidden(
		message = "Forbidden",
		reason: string = ReasonCodes.ROLE_NOT_PERMITTED,
	): ApiError {
		return new ApiError({ code: ErrorCodes.FORBIDDEN, message, reason });
	}

	static conflict(message: string, reason: string, details?: unknown): ApiError {
		return new ApiError({ code: ErrorCodes.CONFLICT, message, reason, details });
	}

	static validation(message: string, reason?: string, details?: unknown): ApiError {
		return new ApiError({ code: ErrorCodes.VALIDATION_ERROR, message, reason, details });
	}
}

/**
 * Contract manifestがPin値と一致しない場合に投げる。
 * 「たぶん互換」と推測して接続を続行しない（Handoff条件）。
 */
export class ContractMismatchError extends Error {
	constructor(
		readonly expected: { contractVersion: string; contractHash: string },
		readonly actual: { contractVersion?: string; contractHash?: string } | null,
		readonly endpoint: string,
	) {
		super(
			`Zerovia contract mismatch at ${endpoint}: expected ${expected.contractVersion}/${expected.contractHash.slice(0, 12)}, ` +
				`got ${actual?.contractVersion ?? "unknown"}/${(actual?.contractHash ?? "unknown").slice(0, 12)}`,
		);
		this.name = "ContractMismatchError";
	}
}

/** Real API接続が失敗したことを、Mockの成功で覆い隠さないための型。 */
export class TransportError extends Error {
	constructor(
		message: string,
		readonly endpoint: string,
		readonly cause?: unknown,
	) {
		super(message);
		this.name = "TransportError";
	}
}

function httpStatusFor(code: string): number {
	switch (code) {
		case ErrorCodes.UNAUTHORIZED:
			return 401;
		case ErrorCodes.FORBIDDEN:
			return 403;
		case ErrorCodes.NOT_FOUND:
			return 404;
		case ErrorCodes.CONFLICT:
			return 409;
		case ErrorCodes.PRECONDITION_FAILED:
			return 412;
		case ErrorCodes.VALIDATION_ERROR:
		case ErrorCodes.UNPROCESSABLE:
			return 422;
		case ErrorCodes.RATE_LIMITED:
			return 429;
		case ErrorCodes.UPSTREAM_ERROR:
			return 502;
		default:
			return 500;
	}
}

export function isApiError(value: unknown): value is ApiError {
	return value instanceof ApiError;
}

export function reasonOf(value: unknown): string | undefined {
	return value instanceof ApiError ? value.reason : undefined;
}
